import { useAction, useAtom } from '@reatom/npm-react'
import React, { useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useEffectOnce } from 'react-use'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { CommentItem } from '@root/entities/comment/ui'
import { Network } from '@root/entities/network'
import { reatomComments, commentsScrollEvent } from '@root/features/comment'
import { FadeOverflow } from '@root/shared/ui-kit'

import { CommentsThreadEmpty } from './comments-thread-empty'
import { CommentsThreadError } from './comments-thread-error'
import { CommentsThreadLoading } from './comments-thread-loading'
import { CommentsThreadMessage } from './comments-thread-message'

import type { Pair } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

export type CommentsThreadProps = {
  pair: Pair.PairMinimal
  ref?: React.Ref<HTMLDivElement>
} & ClassNameProps

export const CommentsThread = ({
  pair,
  className,
  ref: propRef,
}: CommentsThreadProps) => {
  const {
    address: pairAddress,
    quote: { address: quoteTokenAddress },
    base: { address: baseTokenAddress },
    network,
  } = pair
  const threadEndRef = useRef<HTMLDivElement>(null)
  const threadStartRef = useRef<HTMLDivElement>(null)
  const [image, setImage] = useState<string | undefined>(undefined)

  const { ref } = useInView({
    threshold: 0,
    onChange: () => setReplyCommentId('root'),
  })

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomComments({
        pairAddress,
        network,
        baseTokenAddress,
        quoteTokenAddress,
      }),
    [baseTokenAddress, network, pairAddress, quoteTokenAddress],
  )

  const [requestState] = useAtom(model.loadCommentsRequestState)
  const [createCommentRequestState] = useAtom(model.createCommentRequestState)
  const [commentsThread] = useAtom(model.commentsThreadAtom)
  const [replyCommentId, setReplyCommentId] = useAtom(model.replyCommentIdAtom)

  const createCommentAction = useAction(model.createCommentAction)

  useEffectOnce(() => {
    commentsScrollEvent.onCall(() => {
      threadStartRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
      setReplyCommentId('root')
    })
    model.commentCreatedEvent.onCall((_, __, [newComment, totalCount]) => {
      if (totalCount === 0) {
        return
      }

      // skip if comment is a reply
      if (newComment.parentCommentId) {
        return
      }

      // scroll to the bottom of the thread
      setTimeout(() => {
        threadEndRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        })
      }, 500)
    })
  })

  const sortedComments = useMemo(
    () =>
      commentsThread.comments.sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      ),
    [commentsThread.comments],
  )

  const handleMessageSend = (text: string, parentCommentId?: string) => {
    createCommentAction({
      pairAddress,
      req: {
        content: text,
        network: Network.toDto(network),
        parentCommentId,
        imageAttachment: image,
      },
    })
  }
  const handleMessageShow = () => setReplyCommentId('root')
  const handleMessageHide = () => setReplyCommentId(undefined)

  if (AsyncStateN.isLoading(requestState)) {
    return <CommentsThreadLoading className={className} />
  }
  if (AsyncStateN.isError(requestState)) {
    return <CommentsThreadError className={className} />
  }

  if (commentsThread.comments.length === 0) {
    return (
      <Root className={className} ref={threadStartRef}>
        <CommentsThreadEmpty
          ref={propRef}
          className={className}
          pair={pair}
          model={model}
        />
      </Root>
    )
  }

  return (
    <Root className={className} ref={propRef}>
      <Overflow variant="vertical" mode="always" ref={ref}>
        <ThreadStart ref={threadStartRef} />
        <Thread>
          {sortedComments.map((comment) => (
            <CommentItem key={comment.id} comment={comment} model={model} />
          ))}
        </Thread>
        <ThreadEnd ref={threadEndRef} />
        <ThreadMessage
          open={replyCommentId === 'root'}
          isLoading={AsyncStateN.isLoading(createCommentRequestState)}
          onMessageSend={handleMessageSend}
          onMessageShow={handleMessageShow}
          onClose={handleMessageHide}
          imageAttachment={image}
          onImageAttachedChange={setImage}
        />
      </Overflow>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  border-top: 1px solid var(--background-3);

  position: relative;
`

const Overflow = styled(FadeOverflow)`
  height: 100%;
  overflow: auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: auto;
`

const Thread = styled.div`
  display: grid;
  padding-inline: 18px;
  padding-top: 12px;
`

const ThreadMessage = styled(CommentsThreadMessage)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;

  padding: 0 16px 32px 16px;

  background-image: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 1));
`
const ThreadStart = styled.span`
  height: 0;
  display: grid;
`

const ThreadEnd = styled.span`
  height: 90px;
  display: grid;
`
